import type { ReactElement } from 'react';
import React from 'react';
import { isEmpty, isNil } from 'lodash';

import ListItemText from '~/components/core/Atomic/ListItemText';
import MenuItem from '~/components/core/Atomic/MenuItem';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Checkbox from '~/components/ui/atoms/Checkbox';
import { useAutoSelectSingleOption } from '~/hooks/useAutoSelectSingleOption';
import { stringCmp } from '~/Utils';

import OptionChips from '../../../OptionChips';
import { normalizeMultiSelectValue } from '../multiSelectValue';
import type { TextFieldProps } from '../TextField';
import TextField from '../TextField';

// A for the all string option, E for the empty string option, T for the dropdown options
type MultiSelectValueType<T extends string, A extends string = never, E extends string = never> = [A] extends [never]
  ? [E] extends [never]
    ? T
    : T | E
  : [E] extends [never]
    ? T | A
    : T | A | E;

export interface MultiSelectFieldProps<T extends string, A extends string = never, E extends string = never>
  extends Omit<TextFieldProps<T>, 'onChange' | 'value'> {
  renderValue: (value: MultiSelectValueType<T, A, E>[]) => React.ReactNode;
  renderOption?: (option: MultiSelectValueType<T, A, E>) => React.ReactNode;
  options: T[];
  sortAlphabetic?: boolean;
  withOptionChips?: boolean;
  disabledOptions?: string[];
  addAllOption?: A extends never ? false : boolean;
  allOptionValue?: A;
  emptyChoiceValue?: E;
  onChange: (newValues: MultiSelectValueType<T, A, E>[]) => void;
  value?: MultiSelectValueType<T, A, E>[];
  isAllOptionChecksAllCheckboxes?: boolean;
  optionsLoading?: boolean;
  // When the options contain exactly one option, select it automatically. On by default.
  autoSelectSingleOption?: boolean;
}

const MultiSelectField = <T extends string, A extends string = never, E extends string = never>({
  id,
  value,
  disabled,
  renderValue,
  renderOption,
  options,
  sortAlphabetic,
  addAllOption,
  disabledOptions,
  withOptionChips,
  allOptionValue,
  onChange,
  emptyChoiceValue,
  isAllOptionChecksAllCheckboxes,
  optionsLoading = false,
  autoSelectSingleOption = true,
  ...rest
}: MultiSelectFieldProps<T, A, E>): ReactElement => {
  const normalizedValue = normalizeMultiSelectValue(value);
  const selectableOptions = !isEmpty(disabledOptions)
    ? options.filter((option) => !disabledOptions?.includes(option))
    : options;
  useAutoSelectSingleOption<T>({
    // An "All"/empty pseudo-option already gives meaning to an empty value, so skip auto-fill then.
    enabled: autoSelectSingleOption && !disabled && !optionsLoading && !addAllOption && !emptyChoiceValue,
    isEmpty: !normalizedValue.length,
    options: selectableOptions,
    onSelect: (option) => onChange([option]),
  });

  if (sortAlphabetic) {
    options.sort((option1, option2) => {
      if (renderOption) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return stringCmp(renderOption(option1), renderOption(option2));
      }
      if (typeof option1 === 'string' && typeof option2 === 'string') {
        return stringCmp(option1, option2);
      }
      return 0;
    });
  }

  const optionsWithPossibleAll = addAllOption
    ? [allOptionValue as MultiSelectValueType<T, A, E>].concat(options)
    : (options as MultiSelectValueType<T, A, E>[]);

  const isOptionChecked = (option: MultiSelectValueType<T, A, E>) => {
    if (addAllOption && allOptionValue === option && normalizedValue.length === 0) {
      return true;
    }
    return normalizedValue.indexOf(option) > -1;
  };

  const innerOnChange = (newValues: MultiSelectValueType<T, A, E>[]) => {
    const currentValue = normalizedValue;
    const nextValue = newValues;

    if (allOptionValue) {
      if (nextValue.includes(allOptionValue as MultiSelectValueType<T, A, E>)) {
        // allOptionValue was selected and user now selected something else - unselect allOptionValue
        if (currentValue?.includes(allOptionValue as MultiSelectValueType<T, A, E>)) {
          return onChange(nextValue.filter((i) => i !== allOptionValue));
        } else {
          if (isAllOptionChecksAllCheckboxes) {
            // user selected allOptionValue - select everything
            return onChange(optionsWithPossibleAll.filter((option) => !!option) as MultiSelectValueType<T, A, E>[]);
          }
          // user selected allOptionValue - unselect everything else
          return onChange([allOptionValue as MultiSelectValueType<T, A, E>]);
        }
      } else {
        if (isAllOptionChecksAllCheckboxes && currentValue?.includes(allOptionValue as MultiSelectValueType<T, A, E>)) {
          // user unselected allOptionValue - unselect everything
          return onChange([]);
        }
      }
    }

    if (emptyChoiceValue && nextValue.includes(emptyChoiceValue as MultiSelectValueType<T, A, E>)) {
      // emptyChoiceValue was selected and user now selected something else - unselect emptyChoiceValue
      if (currentValue?.includes(emptyChoiceValue as MultiSelectValueType<T, A, E>)) {
        return onChange(nextValue.filter((i) => i !== emptyChoiceValue));
      } else {
        // user selected emptyChoiceValue - unselect everything else
        return onChange([emptyChoiceValue as MultiSelectValueType<T, A, E>]);
      }
    }

    return onChange(newValues);
  };

  const removeSingleValueFromSelectedOptions = (
    id: string | undefined,
    values: MultiSelectValueType<T, A, E>[] | undefined,
    removedValue: MultiSelectValueType<T, A, E>
  ) => {
    const indexValue = values?.indexOf(removedValue);
    !isNil(indexValue) && values?.splice(indexValue, 1);
    innerOnChange([...(values ?? [])]);
  };

  return (
    <div>
      <TextField
        name={id}
        value={normalizedValue}
        disabled={disabled}
        select
        onChange={innerOnChange}
        SelectProps={{
          multiple: true,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          renderValue:
            renderValue ??
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            ((selected: T[]) => (renderOption ? selected.map(renderOption).join(', ') : selected.join(', '))),
        }}
        id={id}
        {...rest}
      >
        {optionsLoading ? (
          <SkeletonTable rowsCount={2} columnsCount={1} wrapperPadding="0" />
        ) : (
          optionsWithPossibleAll.map((option) => {
            if (isNil(option)) return <></>;
            const isChecked = isOptionChecked(option as MultiSelectValueType<T, A, E>);
            return (
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              <MenuItem key={option as string} value={option} disabled={disabledOptions?.includes(option)}>
                <Checkbox checked={isChecked} />
                <ListItemText
                  primary={
                    renderOption && (!addAllOption || (addAllOption && option !== 'All'))
                      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        renderOption(option)
                      : option
                  }
                />
              </MenuItem>
            );
          })
        )}
      </TextField>
      {withOptionChips && (
        <OptionChips
          disabled={disabled}
          onChange={(removedValue) => removeSingleValueFromSelectedOptions(id, normalizedValue, removedValue)}
          values={normalizedValue.map((v) => [
            v,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            renderOption && (!addAllOption || (addAllOption && v !== allOptionValue)) ? renderOption(v) : v,
          ])}
          disabledChips={disabledOptions}
        />
      )}
    </div>
  );
};

export default MultiSelectField;
